import { useMemo } from 'react'
import { handleFetchMore } from '@sketch/components'
import { useGetNotificationsQuery } from '@sketch/gql-types'

import { notificationsQueryOptions } from '../../graphql'
import { useInboxSearchContext } from './InboxSearchContext'
import { buildSearchString } from './searchUtils'

const DAY_MS = 24 * 60 * 60 * 1000

const ENTRIES_PATH = ['me', 'notifications', 'entries']

// Inlined to avoid pulling @sketch/graphql-cache into @sketch/user just for
// pagination dedupe. Notifications always have an identifier.
const notificationDataId = (o: { __typename?: string; identifier?: string }) =>
  o.identifier ? `${o.__typename}:${o.identifier}` : undefined

type Bucket = 'today' | 'this-week' | 'earlier'

const BUCKET_LABELS: Record<Bucket, string> = {
  today: 'Today',
  'this-week': 'This Week',
  earlier: 'Earlier',
}

const getBucket = (createdAt: string): Bucket => {
  const diffDays = (Date.now() - new Date(createdAt).getTime()) / DAY_MS
  if (diffDays < 1) return 'today'
  if (diffDays < 7) return 'this-week'
  return 'earlier'
}

export type InboxItem = {
  id: string
  typename: string | undefined
  state: 'unread' | 'read'
  title: string | null
  subtitle: string | null
  createdAt: string
}

export type InboxGroup = { id: Bucket; label: string; items: InboxItem[] }

export const useInboxNotifications = () => {
  const { search, options, sort } = useInboxSearchContext()

  const searchString = buildSearchString({ search, options })

  const { data, fetchMore, loading, error } = useGetNotificationsQuery({
    ...notificationsQueryOptions,
    variables: {
      filter: {
        search: searchString || undefined,
        states: ['UNREAD', 'DONE', 'READ'],
      },
    },
  })

  const entries = data?.me?.notifications?.entries ?? []
  const after = data?.me?.notifications?.meta?.after ?? null

  // The notifications schema doesn't expose a sort variable, so we sort on the
  // client. The server returns newest-first by default, so 'newest' is a no-op
  // ordering and 'oldest' reverses it.
  const sortedEntries = [...entries].sort((a, b) => {
    const diff =
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    return sort === 'newest' ? diff : -diff
  })

  const buckets =
    sort === 'newest'
      ? (['today', 'this-week', 'earlier'] as const)
      : (['earlier', 'this-week', 'today'] as const)

  const groups: InboxGroup[] = buckets.flatMap(bucket => {
    const bucketEntries = sortedEntries.filter(
      entry => getBucket(entry.createdAt) === bucket
    )
    if (bucketEntries.length === 0) return []
    return [
      {
        id: bucket,
        label: BUCKET_LABELS[bucket],
        items: bucketEntries.map<InboxItem>(entry => ({
          id: entry.identifier,
          typename: entry.__typename,
          state: entry.state === 'UNREAD' ? 'unread' : 'read',
          title: entry.title,
          subtitle: entry.subtitle,
          createdAt: entry.createdAt,
        })),
      },
    ]
  })

  const loadMore = useMemo(
    () =>
      handleFetchMore(fetchMore, ENTRIES_PATH, {
        dataIdFromObject: notificationDataId,
        after,
      }),
    [fetchMore, after]
  )

  return { loading, error, groups, loadMore, after }
}
